"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, PhoneCall, Clock, ShieldCheck, Wrench, TriangleAlert as AlertTriangle, Zap, CircleCheck as CheckCircle2, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const emergencyPoints = [{
  id: 1,
  title: "No Hot Water or Lukewarm Output",
  desc: "Fast diagnostic of heating elements, thermostats, and gas control valves."
}, {
  id: 2,
  title: "Leaking Tanks & Burst Lines",
  desc: "Immediate shutoff assistance and emergency replacement to prevent flood damage."
}, {
  id: 3,
  title: "Tank & Tankless Systems",
  desc: "Certified repair and replacement for all major residential and commercial brands."
}];
const dispatchHighlights = [{
  label: "Average Arrival",
  value: "30-45 Min",
  icon: Clock
}, {
  label: "Pricing Policy",
  value: "Upfront Flat Rates",
  icon: ShieldCheck
}, {
  label: "Availability",
  value: "24/7/365 Service",
  icon: Zap
}];
export default function WaterHeater() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="waterheaterhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Decorative subtle background accents */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-40">
        <div className="h-[420px] w-[420px] rounded-full bg-accent/15 blur-3xl" />
        <div className="h-[520px] w-[520px] rounded-full bg-primary/5 blur-3xl -ml-36 mt-28" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          
          {/* Main Hero Column */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Urgency Alert Badge */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="destructive" className="px-3 py-1 font-semibold text-xs tracking-wide uppercase">
                <AlertTriangle className="size-3.5 mr-1 animate-pulse" />
                Emergency Hot Water Dispatch
              </Badge>
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium">
                Jonesboro, GA & Clayton County
              </Badge>
            </div>

            {/* Main Headline */}
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl/tight font-bold text-foreground font-default tracking-tight">
                24/7 Emergency <span className="text-primary underline decoration-accent decoration-4 underline-offset-4">Water Heater</span> Repair & Replacement
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl font-default leading-relaxed">
                No hot water or leaking tank? Our experienced plumbers are ready to deploy immediately across Jonesboro. We restore your hot water today with honest upfront pricing.
              </p>
            </div>

            {/* Primary Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
              <Button asChild size="lg" className="bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md font-semibold text-base h-13 px-6 rounded-lg">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary/30 text-foreground hover:bg-secondary h-13 px-6 rounded-lg font-medium text-base shadow-2xs">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Instant Dispatch</span>
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Key Emergency Indicators */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4">
              {dispatchHighlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.label} data-index={i} className="flex items-center gap-3 rounded-lg bg-card p-3 border border-border/70 shadow-2xs">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent/20 text-accent-foreground">
                      <IconComponent className="size-4.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground leading-tight">{item.label}</p>
                      <p className="text-sm font-semibold text-foreground truncate">{item.value}</p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Bulleted Emergency Issues Handled */}
            <div className="pt-2 border-t border-border/80 space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Common Problems We Fix Today:</p>
              <div className="space-y-2">
                {emergencyPoints.map((pt, i) => <div key={pt.id} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4.5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{pt.title}: </span>
                      <span className="text-sm text-muted-foreground">{pt.desc}</span>
                    </div>
                  </div>)}
              </div>
            </div>

          </div>

          {/* Visual Showcase & Fast Response Card Column */}
          <div className="lg:col-span-5 space-y-4">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative">
              {/* Image Container with Responsive Attributes */}
              <div className="relative overflow-hidden rounded-2xl border border-border shadow-lg bg-muted aspect-4/3 sm:aspect-16/10 lg:aspect-4/3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_repair.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_repair.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_repair.png-wvc-sizes" alt="Jonesboro certified technician inspecting a residential water heater tank" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                
                {/* Visual badge overlay */}
                <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-xs border border-border px-3.5 py-2 rounded-lg shadow-md flex items-center gap-2.5">
                  <div className="size-2.5 rounded-full bg-destructive animate-ping" />
                  <span className="text-xs font-bold text-foreground">Active Crew in Jonesboro</span>
                </div>

                <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-3.5 py-1.5 rounded-md text-xs font-medium shadow-md flex items-center gap-1.5">
                  <Flame className="size-3.5 text-accent" />
                  <span>Same-Day Replacement</span>
                </div>
              </div>

              {/* Urgency Guarantee Card */}
              <Card className="mt-4 border-primary/20 bg-card shadow-md py-4">
                <CardContent className="px-5 py-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Wrench className="size-4 text-primary" />
                      <h3 className="font-semibold text-foreground text-sm font-default">Fast Tank Drain & Diagnostic</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Licensed, insured & local Clayton County specialists.</p>
                  </div>
                  <Button asChild size="sm" variant="secondary" className="shrink-0 font-semibold text-xs border border-border">
                    <Link to="/services/">
                      All Services
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}